// ─────────────────────────────────────────────────────────────────────────────
// fractalMusic.js
//
// The instrumental fractal-music engine. Self-contained: no React, no App.js
// imports. App.js calls into this. Ported from the validated sandbox
// (hear-it-deep.html) — the model proven over the June 25 design session:
//
//   • TRUE escape values (integers; smoothing/ln term dropped for music).
//   • Region = maximal run of pixels with the SAME integer escape value.
//   • escape → (instrument, scaleDegree, octaveOffset) via an editable TABLE.
//   • PITCH = scaleDegree realized within a GLOBAL key+scale (transposable).
//   • Degree from escape PERCENTILE (distribution-aware) so deep-zoom values
//     (e.g. 125–3484) spread musically instead of flying off the top.
//   • Location-invariant within an image: same escape → same note everywhere.
//
// Audio is produced by smplr (Soundfont). The engine is given an AudioContext
// (reused from the app's ensureAudio) so it coexists with the oscillator synth.
// ─────────────────────────────────────────────────────────────────────────────

import { Soundfont } from "smplr";

// ── Voice cap ────────────────────────────────────────────────────────────────
// Per-region simultaneous-voice cap. SINGLE SOURCE OF TRUTH: both playback
// (playRegionLoudness, below) and the read-only meter (scanDiagnostics.js, which
// imports this) use THIS constant, so the meter can never disagree with what
// actually sounds. Change it here and both move together.
export const VOICE_CAP = 6;

// ── Static musical data ──────────────────────────────────────────────────────
// instrument key → General-MIDI Soundfont name smplr recognizes.
export const INSTRUMENTS = {
  harp: "orchestral_harp",
  cello: "cello",
  clarinet: "clarinet",
  oboe: "oboe",
  violin: "violin",
  flute: "flute",
};
export const INST_ORDER = ["harp", "cello", "clarinet", "oboe", "violin", "flute"];

// Full General MIDI instrument names smplr's Soundfont loader accepts, grouped
// for the sound-editor picker. Standard GM names (lowercase, underscores).
export const GM_INSTRUMENTS = {
  Keyboards: ["acoustic_grand_piano","bright_acoustic_piano","electric_grand_piano","honkytonk_piano","electric_piano_1","electric_piano_2","harpsichord","clavinet","celesta"],
  Tuned_Percussion: ["glockenspiel","music_box","vibraphone","marimba","xylophone","tubular_bells","dulcimer"],
  Organ: ["drawbar_organ","percussive_organ","rock_organ","church_organ","reed_organ","accordion","harmonica"],
  Guitar: ["acoustic_guitar_nylon","acoustic_guitar_steel","electric_guitar_jazz","electric_guitar_clean","electric_guitar_muted","overdriven_guitar","distortion_guitar"],
  Bass: ["acoustic_bass","electric_bass_finger","electric_bass_pick","fretless_bass","slap_bass_1","slap_bass_2","synth_bass_1","synth_bass_2"],
  Strings: ["violin","viola","cello","contrabass","tremolo_strings","pizzicato_strings","orchestral_harp","timpani","string_ensemble_1","string_ensemble_2"],
  Brass: ["trumpet","trombone","tuba","muted_trumpet","french_horn","brass_section"],
  Reed: ["soprano_sax","alto_sax","tenor_sax","baritone_sax","oboe","english_horn","bassoon","clarinet"],
  Pipe: ["piccolo","flute","recorder","pan_flute","blown_bottle","shakuhachi","whistle","ocarina"],
  Ensemble: ["choir_aahs","voice_oohs","synth_choir"],
};

// instrument key → GM soundfont name. Default six keep their INSTRUMENTS mapping;
// any other value is already a GM soundfont name.
export function sfNameFor(instrument) {
  return INSTRUMENTS[instrument] || instrument;
}

export const SCALES = {
  major:      [0, 2, 4, 5, 7, 9, 11],
  minor:      [0, 2, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9],
  blues:      [0, 3, 5, 6, 7, 10],
  wholetone:  [0, 2, 4, 6, 8, 10],
  // All twelve semitones. One escape step = one semitone rather than one
  // scale step, so nothing is snapped away: melodies needing accidentals
  // become representable, and the escape field is heard unquantized.
  chromatic:  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};
export const ROOTS = { C: 0, Db: 1, D: 2, Eb: 3, E: 4, F: 5, Gb: 6, G: 7, Ab: 8, A: 9, Bb: 10, B: 11 };

const NN = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export const midiName = (m) => NN[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);

const DEGREE_SPAN = 14; // escape range spans ~this many scale degrees (≈2 octaves)

// ── Pitch realization within the global key ──────────────────────────────────
// degree is 0-based into the scale and wraps across octaves.
export function realize(degree, octaveOffset, key) {
  const scale = SCALES[key.scale] || SCALES.major;
  const n = scale.length;
  const deg = ((degree % n) + n) % n;
  const octFromDegree = Math.floor(degree / n);
  const rootMidi = (key.baseOctave + 1) * 12 + (ROOTS[key.root] ?? 0); // C4=60 at baseOctave 4
  return rootMidi + scale[deg] + 12 * (octFromDegree + octaveOffset);
}

// ── Escape-value distribution (percentile mapping) ───────────────────────────
// Build once per image from the TRUE escape values (fractalData.image), so
// escape→degree spreads across the image's actual distribution. Excludes in-set
// pixels (escape >= maxIter). Returns a sorted typed array + a lookup closure.
export function buildEscapeDistribution(image, maxIter) {
  const escaped = [];
  for (let i = 0; i < image.length; i++) {
    const e = image[i];
    if (e < maxIter) escaped.push(e);
  }
  escaped.sort((a, b) => a - b);
  const sorted = escaped;
  return function percentile(e) {
    if (sorted.length === 0) return 0;
    let lo = 0, hi = sorted.length - 1, ans = 0;
    while (lo <= hi) {
      const m = (lo + hi) >> 1;
      if (sorted[m] <= e) { ans = m; lo = m + 1; } else hi = m - 1;
    }
    return ans / (sorted.length - 1);
  };
}

// ── SOUND PALETTE (stops) — sibling of the color palette ─────────────────────
// The color palette is a list of stops { t, r, g, b } that samplePalette()
// interpolates. The SOUND palette is the same idea: stops
//   { t, instrument, scaleDegree, octaveOffset }
// with t ascending 0→1. A region's escape value → percentile t → the stop whose
// range contains t. UNLIKE color (which lerps RGB), sound SNAPS to the lower
// stop: an instrument can't blend into another, and a region plays ONE
// instrument at ONE in-key pitch. This is the user-composed mapping the sound
// editor edits.

// Default sound palette — the "climbing" starting point (sound editor edits it).
export const DEFAULT_SOUND_STOPS = [
  { t: 0.00, instrument: "harp",     scaleDegree: 0,  octaveOffset: 0 },
  { t: 0.20, instrument: "cello",    scaleDegree: 2,  octaveOffset: 0 },
  { t: 0.40, instrument: "clarinet", scaleDegree: 4,  octaveOffset: 0 },
  { t: 0.60, instrument: "oboe",     scaleDegree: 6,  octaveOffset: 0 },
  { t: 0.80, instrument: "violin",   scaleDegree: 8,  octaveOffset: 0 },
  { t: 1.00, instrument: "flute",    scaleDegree: 11, octaveOffset: 1 },
];

// Sample the sound palette at position t∈[0,1]: return the stop in effect.
// SNAPS to the lower bracketing stop (no interpolation — discrete instruments).
export function sampleSoundPalette(stops, t) {
  const tt = t < 0 ? 0 : t > 1 ? 1 : t;
  if (tt <= stops[0].t) return stops[0];
  for (let i = 0; i < stops.length - 1; i++) {
    if (tt >= stops[i].t && tt < stops[i + 1].t) return stops[i];
  }
  return stops[stops.length - 1];
}

// Build a lookup escape → { instrument, scaleDegree, octaveOffset } from a sound
// palette + the image's percentile function.
export function makeSoundPaletteLookup(percentile, stops) {
  const pal = stops && stops.length ? stops : DEFAULT_SOUND_STOPS;
  return function tableFor(esc) {
    const t = percentile(esc);
    const s = sampleSoundPalette(pal, t);
    return { instrument: s.instrument, scaleDegree: s.scaleDegree, octaveOffset: s.octaveOffset };
  };
}

// ── Default table generator (legacy, kept for the existing instrumental mode) ─
// escape integer → { instrument, scaleDegree, octaveOffset } via percentile.
export function makeDefaultTableLookup(percentile) {
  return function tableFor(esc) {
    const pct = percentile(esc);
    const instrument = INST_ORDER[Math.min(INST_ORDER.length - 1, Math.floor(pct * INST_ORDER.length))];
    const scaleDegree = Math.round(pct * DEGREE_SPAN);
    const octaveOffset = 0;
    return { instrument, scaleDegree, octaveOffset };
  };
}


// ═══════════════════════════════════════════════════════════════════════════
// LOUDNESS MODEL — the sound editor as a mirror of the color editor.
// Supersedes the stops-based sound palette above. Each instrument is a CHANNEL
// (like R/G/B) defined by (initial, step, direction, octave). For an escape
// value's band position, an instrument's LOUDNESS = clamp(initial ± step·n).
// All instruments with loudness > 0 sound simultaneously, each playing the
// scale degree derived from the band, in its own octave, within the global key.
// In-set (escape >= maxIter) = silence. Sparse by design: most instruments stay
// at initial=0/step=0 (silent); the composer activates a few.
// ═══════════════════════════════════════════════════════════════════════════

// Max loudness level N (the sonic analog of color's 255). MIDI velocity tops at
// 127; we use 100 as a musical default ceiling so "max" isn't harsh.
export const DEFAULT_MAX_LOUD = 100;

// A loudness palette entry — one instrument channel.
//   instrument : GM soundfont name (e.g. "violin")
//   initial    : loudness at band 0 (0..N)
//   step       : loudness change per band
//   direction  : +1 (louder toward boundary / higher escape) or -1 (toward interior)
//   octave     : octave offset for this instrument's pitch (register spread)
// A palette is an array of these. Empty array / all-zero = silence.
export const DEFAULT_LOUDNESS_PALETTE = [
  { instrument: "violin", initial: 0, step: 1.2, direction: 1, octave: 1 },
];

// Clamp a value into [0, N]. (Bob chose CLAMP over color's mod-wrap, so an
// instrument swells and HOLDS at max rather than snapping max→silent.)
function clampLoud(v, N) {
  if (v < 0) return 0;
  if (v > N) return N;
  return v;
}

// Number of scale degrees the escape range spans (≈2 octaves). Reuses the same
// idea as the stops model's DEGREE_SPAN so pitch still tracks escape.
const LOUD_DEGREE_SPAN = 14;

// Build a lookup: escape value → array of notes to play this band.
// Returns a function escapeToNotes(esc) →
//   [] for in-set/silence, else [{ instrument, midi, velocity }, ...]
// for every instrument whose computed loudness > 0.
//
//   percentile : escape → 0..1 (from buildEscapeDistribution)
//   palette    : array of loudness entries (above)
//   key        : { root, scale, baseOctave }
//   maxLoud    : N (default DEFAULT_MAX_LOUD)
//   maxIter    : in-set threshold; esc >= maxIter → silence
// ── Chordal harmony option: snap fractal notes to a chord progression ────────
// OPTIONAL, off by default ("fractal" mode = pure sonification, unchanged).
// When enabled ("chordal" mode), the fractal still drives pitch contour and
// register, but each note is snapped to the nearest tone of the current chord
// in a I - V - IV - I progression (minor: i - V - iv - i) advancing with the
// escape-position t (0..1). A middle path between pure sonification and imposed
// composition: the math still plays, but within a tonal frame.
//
// Chords are derived FROM THE SELECTED SCALE (degrees 0/2/4 of the tonic, the
// 5th, and the 4th), so they are correct in any key AND any scale the user
// picks. In minor, the dominant's third is raised a semitone (the harmonic-minor
// leading tone) so the V chord pulls home the way tonal minor harmony does.
//
// Build a triad from scale degrees, as semitone offsets from the tonic root.
function triadFromScale(scale, startDegree, raiseThird) {
  const n = scale.length;
  const at = (d) => {
    const idx = ((d % n) + n) % n;
    const oct = Math.floor(d / n);
    return scale[idx] + 12 * oct;
  };
  const root = at(startDegree);
  let third = at(startDegree + 2);
  const fifth = at(startDegree + 4);
  if (raiseThird) third += 1; // harmonic-minor leading tone in the dominant
  return [root, third, fifth];
}
// Choose the chord for escape-position t (0..1), derived from the active scale.
function chordForT(t, key) {
  const scale = SCALES[key.scale] || SCALES.major;
  const isMinor = key.scale === "minor";
  if (t < 0.30) return triadFromScale(scale, 0, false);           // tonic  (i / I)
  if (t < 0.55) return triadFromScale(scale, 4, isMinor);         // dominant (V) — raised 3rd in minor
  if (t < 0.78) return triadFromScale(scale, 3, false);           // subdominant (iv / IV)
  return triadFromScale(scale, 0, false);                          // home again at the climax
}
// Snap a MIDI note to the nearest pitch belonging to the chord, keeping register
// close to the fractal-derived note. rootPc = pitch class of the key root.
function snapToChord(midi, chordOffsets, rootPc) {
  const pcs = chordOffsets.map((o) => (((rootPc + o) % 12) + 12) % 12);
  let best = midi, bestDist = Infinity;
  const baseOct = Math.floor(midi / 12);
  for (let oct = baseOct - 1; oct <= baseOct + 1; oct++) {
    for (let k = 0; k < pcs.length; k++) {
      const cand = oct * 12 + pcs[k];
      const d = Math.abs(cand - midi);
      if (d < bestDist) { bestDist = d; best = cand; }
    }
  }
  return best;
}
// Snap a realized note to the chord progression at escape-position t.
export function chordalSnap(midi, t, key) {
  const rootPc = ROOTS[key.root] ?? 0;
  return snapToChord(midi, chordForT(t, key), rootPc);
}
export function makeLoudnessLookup(percentile, palette, key, maxLoud, maxIter, harmony) {
  const N = maxLoud || DEFAULT_MAX_LOUD;
  const pal = palette && palette.length ? palette : DEFAULT_LOUDNESS_PALETTE;
  return function escapeToNotes(esc) {
    // Silence inside the set.
    if (esc >= maxIter) return [];
    const t = percentile(esc);            // 0..1 position in the escape distribution
    const band = Math.round(t * 255);     // band index n (0..255), like a palette page
    const degree = Math.round(t * LOUD_DEGREE_SPAN); // pitch tracks escape, in-key
    const notes = [];
    for (let i = 0; i < pal.length; i++) {
      const inst = pal[i];
      const dir = inst.direction === -1 ? -1 : 1;
      const loud = clampLoud((inst.initial || 0) + dir * (inst.step || 0) * band, N);
      if (loud > 0) {
        let midi = realize(degree, inst.octave || 0, key);
        if (harmony) midi = chordalSnap(midi, t, key);
        const velocity = Math.max(1, Math.min(127, Math.round(loud)));
        notes.push({ instrument: inst.instrument, midi, velocity });
      }
    }
    return notes;
  };
}

// Convenience: list the distinct instruments used by a loudness palette (for
// pre-loading soundfonts before playback).
export function instrumentsInLoudnessPalette(palette) {
  const pal = palette && palette.length ? palette : DEFAULT_LOUDNESS_PALETTE;
  const set = new Set();
  for (const inst of pal) {
    if ((inst.initial || 0) !== 0 || (inst.step || 0) !== 0) set.add(inst.instrument);
  }
  return Array.from(set);
}

// Compute the notes sounding at a specific BAND index (0..255) for a loudness
// palette — the band-indexed sibling of makeLoudnessLookup's inner logic. Used
// by the sound editor's live preview (the popup gives a band directly, not an
// escape value). Returns [{ instrument, midi, velocity }] for loudness > 0.
export function notesAtBand(palette, key, band, maxLoud, harmony) {
  const N = maxLoud || DEFAULT_MAX_LOUD;
  const pal = palette && palette.length ? palette : DEFAULT_LOUDNESS_PALETTE;
  const b = band < 0 ? 0 : (band > 255 ? 255 : band);
  const degree = Math.round((b / 255) * LOUD_DEGREE_SPAN);
  const notes = [];
  for (let i = 0; i < pal.length; i++) {
    const inst = pal[i];
    const dir = inst.direction === -1 ? -1 : 1;
    const loud = clampLoud((inst.initial || 0) + dir * (inst.step || 0) * b, N);
    if (loud > 0) {
      let midi = realize(degree, inst.octave || 0, key);
      if (harmony) midi = chordalSnap(midi, b / 255, key);
      notes.push({
        instrument: inst.instrument,
        midi: midi,
        velocity: Math.max(1, Math.min(127, Math.round(loud))),
      });
    }
  }
  return notes;
}
// ── Integer-exact region extraction down a line ──────────────────────────────
// A region = a maximal run of pixels with the SAME integer escape value.
// `getEscapeAt(i)` returns the escape value of the i-th pixel along the scan
// line (0..lineLen-1). minHeight merges runs shorter than it into the previous
// region (note-rate control near the boundary). In-set pixels (escape>=maxIter)
// form their own regions and are flagged.
export function extractIntegerRegions(getEscapeAt, lineLen, maxIter, minHeight = 1) {
  const raw = [];
  let start = 0;
  let cur = getEscapeAt(0);
  for (let i = 1; i < lineLen; i++) {
    const e = getEscapeAt(i);
    if (e !== cur) {
      raw.push({ start, end: i - 1, height: i - start, escape: cur, inSet: cur >= maxIter });
      start = i;
      cur = e;
    }
  }
  raw.push({ start, end: lineLen - 1, height: lineLen - start, escape: cur, inSet: cur >= maxIter });
  if (minHeight <= 1) return raw;
  const out = [];
  for (const r of raw) {
    if (out.length && r.height < minHeight) {
      const p = out[out.length - 1];
      p.end = r.end;
      p.height += r.height;
      // keep p.escape (the surviving region's identity)
    } else {
      out.push({ ...r });
    }
  }
  return out;
}

// ── The engine ───────────────────────────────────────────────────────────────
// Holds loaded smplr instruments and the global key. Created once, given the
// app's AudioContext. Instruments load lazily/async.
export class FractalMusicEngine {
  constructor(audioContext) {
    this.ctx = audioContext;
    this.players = {};          // sf name → Soundfont instance
    this.loaded = false;
    this.loading = null;        // Promise while loading
    this.key = { root: "Eb", scale: "major", baseOctave: 4 };
    this.table = null;          // optional user table override (escape → cell)
  }

  setKey(partialKey) {
    this.key = { ...this.key, ...partialKey };
  }

  // Ensure a single GM instrument (by soundfont name) is loaded. On-demand:
  // the sound editor can reference any of ~80 GM instruments, so we load each
  // the first time it's needed rather than preloading everything.
  async ensureInstrument(sf) {
    if (this.players[sf]) return this.players[sf];
    if (!this._pending) this._pending = {};
    if (this._pending[sf]) return this._pending[sf];
    this._pending[sf] = (async () => {
      const inst = new Soundfont(this.ctx, { instrument: sf });
      await inst.load;
      this.players[sf] = inst;
      delete this._pending[sf];
      return inst;
    })();
    return this._pending[sf];
  }

  // Preload the default six (for the initial instrumental mode). Idempotent.
  async load() {
    if (this.loaded) return;
    if (this.loading) return this.loading;
    const sfNames = [...new Set(INST_ORDER.map((k) => INSTRUMENTS[k]))];
    this.loading = Promise.all(sfNames.map((sf) => this.ensureInstrument(sf)))
      .then(() => { this.loaded = true; this.loading = null; });
    return this.loading;
  }

  // Preload every instrument referenced by a sound palette (so a scan doesn't
  // stutter while loading mid-playback). Call before starting an instrumental scan.
  async loadForPalette(stops) {
    const names = [...new Set((stops || []).map((s) => sfNameFor(s.instrument)))];
    await Promise.all(names.map((sf) => this.ensureInstrument(sf)));
    this.loaded = true;
  }

  // Resolve a region's escape value to { instrument, sf, midi } using the table
  // (or the supplied lookup) and the current global key.
  resolveNote(escape, tableLookup) {
    const cell = (this.table && this.table[escape]) || tableLookup(escape);
    const midi = realize(cell.scaleDegree, cell.octaveOffset, this.key);
    return { instrument: cell.instrument, sf: sfNameFor(cell.instrument), midi };
  }

  // Play a single region's note now-ish. durationSec is the sustain.
  playRegion(escape, tableLookup, when, durationSec, velocity = 82) {
    const { sf, midi } = this.resolveNote(escape, tableLookup);
    const p = this.players[sf];
    if (p) p.start({ note: midi, velocity, time: when, duration: durationSec });
  }
  // LOUDNESS MODEL playback: the lookup returns an ARRAY of notes
  // ({ instrument, midi, velocity }) — all sounding simultaneously for this
  // region. Fires each on its own instrument. Empty array = silence.
  playRegionLoudness(escape, loudnessLookup, when, durationSec) {
    let notes = loudnessLookup(escape);
    if (!notes || notes.length === 0) return;
    // Voice cap: at dense regions many instruments activate at once. This bound
    // keeps a pathological column from spawning unlimited voices, but is set to
    // match the largest supported orchestration (17 instruments) so a fully
    // scored region blooms as composed rather than being trimmed. Raised from 6
    // (a defensive value from when Bluetooth dropouts were mistaken for voice
    // overload) now that stable wired audio is the norm.
    const CAP = VOICE_CAP;
    if (notes.length > CAP) {
      notes = notes.slice().sort((a, b) => b.velocity - a.velocity).slice(0, CAP);
    }
    for (let i = 0; i < notes.length; i++) {
      const note = notes[i];
      const p = this.players[sfNameFor(note.instrument)];
      if (p) p.start({ note: note.midi, velocity: note.velocity, time: when, duration: durationSec });
    }
  }
  // ── LIVE PREVIEW (sound editor sustained chord) ──────────────────────────
  // The sound editor drives a sustained "chord" so the composer hears the
  // palette at one escape band and, while dragging a slider, hears only that
  // instrument change against the others. Notes are held (long duration) and
  // stopped/restarted per instrument. Mirrors the validated sound-preview
  // sandbox but uses this engine's loaded players.
  previewVoice(notes) {
    // notes: [{ instrument, midi, velocity }] — replace the whole held chord.
    this.previewStop();
    this._preview = {};
    if (!notes) return;
    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      const p = this.players[sfNameFor(n.instrument)];
      if (p) {
        const stop = p.start({ note: n.midi, velocity: n.velocity, duration: 9999 });
        this._preview[n.instrument] = { stop, midi: n.midi };
      }
    }
  }
  previewUpdateOne(instrument, note) {
    // Update ONE instrument in the held chord. note = { midi, velocity } or null
    // (null silences this instrument). Others keep sounding unchanged.
    if (!this._preview) this._preview = {};
    const cur = this._preview[instrument];
    if (cur && cur.stop) { try { cur.stop(); } catch (_) {} }
    if (note) {
      const p = this.players[sfNameFor(instrument)];
      if (p) {
        const stop = p.start({ note: note.midi, velocity: note.velocity, duration: 9999 });
        this._preview[instrument] = { stop, midi: note.midi };
      } else {
        delete this._preview[instrument];
      }
    } else {
      delete this._preview[instrument];
    }
  }
  previewStop() {
    if (this._preview) {
      for (const k in this._preview) {
        try { if (this._preview[k] && this._preview[k].stop) this._preview[k].stop(); } catch (_) {}
      }
    }
    this._preview = {};
  }
}
